import { Database } from '@/integrations/supabase/types';

export type UserRole = Database['public']['Enums']['user_role'] | 'vendedor' | 'financeiro' | 'ai_agent' | 'user';

export interface Permission {
  id: string;
  name: string;
  description: string;
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'manage';
}

export interface RolePermissions {
  role: UserRole;
  permissions: Permission[];
}

// Definição das permissões por recurso
export const PERMISSIONS = {
  // Gestão de Leads
  LEADS: {
    CREATE: 'leads:create',
    READ: 'leads:read',
    UPDATE: 'leads:update',
    DELETE: 'leads:delete',
    MANAGE: 'leads:manage',
  },
  
  // Gestão de Clientes
  CLIENTS: {
    CREATE: 'clients:create',
    READ: 'clients:read',
    UPDATE: 'clients:update',
    DELETE: 'clients:delete',
    MANAGE: 'clients:manage',
  },
  
  // Gestão de Contratos
  CONTRACTS: {
    CREATE: 'contracts:create',
    READ: 'contracts:read',
    UPDATE: 'contracts:update',
    DELETE: 'contracts:delete',
    MANAGE: 'contracts:manage',
  },
  
  // Gestão de Propostas
  PROPOSALS: {
    CREATE: 'proposals:create',
    READ: 'proposals:read',
    UPDATE: 'proposals:update',
    DELETE: 'proposals:delete',
    MANAGE: 'proposals:manage',
  },
  
  // Gestão de Usuários
  USERS: {
    CREATE: 'users:create',
    READ: 'users:read',
    UPDATE: 'users:update',
    DELETE: 'users:delete',
    MANAGE: 'users:manage',
  },
  
  // Relatórios
  REPORTS: {
    READ: 'reports:read',
    EXPORT: 'reports:export',
    MANAGE: 'reports:manage',
  },
  
  // Configurações do Sistema
  SETTINGS: {
    READ: 'settings:read',
    UPDATE: 'settings:update',
    MANAGE: 'settings:manage',
  },
  
  // Dimensionamento
  DIMENSIONING: {
    CREATE: 'dimensioning:create',
    READ: 'dimensioning:read',
    UPDATE: 'dimensioning:update',
    DELETE: 'dimensioning:delete',
    MANAGE: 'dimensioning:manage',
  },
  
  // Gestão de Distribuidores
  DISTRIBUTORS: {
    CREATE: 'distributors:create',
    READ: 'distributors:read',
    UPDATE: 'distributors:update',
    DELETE: 'distributors:delete',
    MANAGE: 'distributors:manage',
  },
  
  // Gestão de Parceiros
  PARCEIROS: {
    CREATE: 'parceiros:create',
    READ: 'parceiros:read',
    UPDATE: 'parceiros:update',
    DELETE: 'parceiros:delete',
    MANAGE: 'parceiros:manage',
    VIEW_METRICS: 'parceiros:metrics',
  },
  
  // Gestão de Vendedores
  VENDEDORES: {
    CREATE: 'vendedores:create',
    READ: 'vendedores:read',
    UPDATE: 'vendedores:update',
    DELETE: 'vendedores:delete',
    MANAGE: 'vendedores:manage',
    VIEW_METRICS: 'vendedores:metrics',
  },

  // Comissões (vendedores e parceiros)
  COMISSOES: {
    READ: 'comissoes:read',
    MANAGE: 'comissoes:manage',
  },
} as const;

// Mapeamento de permissões por perfil
export const ROLE_PERMISSIONS: RolePermissions[] = [
  {
    role: 'admin',
    permissions: [
      // Admin tem acesso total a tudo
      ...Object.values(PERMISSIONS.LEADS).map(perm => ({
        id: perm,
        name: 'Gestão Completa de Leads',
        description: 'Acesso total ao módulo de leads',
        resource: 'leads',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.CLIENTS).map(perm => ({
        id: perm,
        name: 'Gestão Completa de Clientes',
        description: 'Acesso total ao módulo de clientes',
        resource: 'clients',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.CONTRACTS).map(perm => ({
        id: perm,
        name: 'Gestão Completa de Contratos',
        description: 'Acesso total ao módulo de contratos',
        resource: 'contracts',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.PROPOSALS).map(perm => ({
        id: perm,
        name: 'Gestão Completa de Propostas',
        description: 'Acesso total ao módulo de propostas',
        resource: 'proposals',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.USERS).map(perm => ({
        id: perm,
        name: 'Gestão Completa de Usuários',
        description: 'Acesso total ao módulo de usuários',
        resource: 'users',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.REPORTS).map(perm => ({
        id: perm,
        name: 'Relatórios Completos',
        description: 'Acesso total aos relatórios',
        resource: 'reports',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.SETTINGS).map(perm => ({
        id: perm,
        name: 'Configurações do Sistema',
        description: 'Acesso total às configurações',
        resource: 'settings',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.DIMENSIONING).map(perm => ({
        id: perm,
        name: 'Dimensionamento Completo',
        description: 'Acesso total ao módulo de dimensionamento',
        resource: 'dimensioning',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.DISTRIBUTORS).map(perm => ({
        id: perm,
        name: 'Gestão Completa de Distribuidores',
        description: 'Acesso total ao módulo de distribuidores',
        resource: 'distributors',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.PARCEIROS).map(perm => ({
        id: perm,
        name: 'Gestão Completa de Parceiros',
        description: 'Acesso total ao módulo de parceiros',
        resource: 'parceiros',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.VENDEDORES).map(perm => ({
        id: perm,
        name: 'Gestão Completa de Vendedores',
        description: 'Acesso total ao módulo de vendedores',
        resource: 'vendedores',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.COMISSOES).map(perm => ({
        id: perm,
        name: 'Gestão de Comissões',
        description: 'Acesso total ao módulo de comissões',
        resource: 'comissoes',
        action: perm.split(':')[1] as string,
      })),
    ],
  },
  {
    role: 'vendedor',
    permissions: [
      // Vendedor pode gerenciar leads dos seus parceiros e criar propostas
      PERMISSIONS.LEADS.CREATE,
      PERMISSIONS.LEADS.READ,
      PERMISSIONS.LEADS.UPDATE,
      PERMISSIONS.CLIENTS.CREATE,
      PERMISSIONS.CLIENTS.READ,
      PERMISSIONS.CLIENTS.UPDATE,
      PERMISSIONS.PROPOSALS.CREATE,
      PERMISSIONS.PROPOSALS.READ,
      PERMISSIONS.PROPOSALS.UPDATE,
      PERMISSIONS.CONTRACTS.READ,
      PERMISSIONS.CONTRACTS.UPDATE,
      PERMISSIONS.DIMENSIONING.CREATE,
      PERMISSIONS.DIMENSIONING.READ,
      PERMISSIONS.DIMENSIONING.UPDATE,
      PERMISSIONS.DISTRIBUTORS.READ,
      PERMISSIONS.PARCEIROS.READ,
      PERMISSIONS.PARCEIROS.CREATE,
      PERMISSIONS.PARCEIROS.VIEW_METRICS,
      // Vendedor não tem acesso a relatórios
      // PERMISSIONS.REPORTS.READ removido para vendedores
      // Vendedor pode ver apenas seu próprio card na página de vendedores
      PERMISSIONS.VENDEDORES.READ,
      PERMISSIONS.VENDEDORES.VIEW_METRICS,
      PERMISSIONS.COMISSOES.READ,
    ].map(perm => ({
      id: perm,
      name: `Acesso a ${perm.split(':')[0]}`,
      description: `Permissão para ${perm.split(':')[1]} em ${perm.split(':')[0]}`,
      resource: perm.split(':')[0],
      action: perm.split(':')[1] as string,
    })),
  },
  {
    role: 'representante',
    permissions: [
      // Representante pode gerenciar leads, clientes e propostas próprias
      PERMISSIONS.LEADS.CREATE,
      PERMISSIONS.LEADS.READ,
      PERMISSIONS.LEADS.UPDATE,
      PERMISSIONS.CLIENTS.CREATE,
      PERMISSIONS.CLIENTS.READ,
      PERMISSIONS.CLIENTS.UPDATE,
      PERMISSIONS.PROPOSALS.CREATE,
      PERMISSIONS.PROPOSALS.READ,
      PERMISSIONS.PROPOSALS.UPDATE,
      PERMISSIONS.CONTRACTS.READ,
      PERMISSIONS.DIMENSIONING.CREATE,
      PERMISSIONS.DIMENSIONING.READ,
      PERMISSIONS.DIMENSIONING.UPDATE,
      PERMISSIONS.DISTRIBUTORS.READ,
      PERMISSIONS.REPORTS.READ,
    ].map(perm => ({
      id: perm,
      name: `Acesso a ${perm.split(':')[0]}`,
      description: `Permissão para ${perm.split(':')[1]} em ${perm.split(':')[0]}`,
      resource: perm.split(':')[0],
      action: perm.split(':')[1] as string,
    })),
  },
  {
    role: 'parceiro',
    permissions: [
      // Parceiro pode ler e atualizar seus próprios leads (incluindo mover entre etapas)
      PERMISSIONS.LEADS.READ,
      PERMISSIONS.LEADS.UPDATE,
      PERMISSIONS.CLIENTS.READ,
      PERMISSIONS.PROPOSALS.READ,
      PERMISSIONS.CONTRACTS.READ,
      PERMISSIONS.DIMENSIONING.READ,
      PERMISSIONS.DIMENSIONING.CREATE,
      PERMISSIONS.DIMENSIONING.UPDATE,
      PERMISSIONS.PROPOSALS.CREATE,
      PERMISSIONS.PROPOSALS.UPDATE,
      PERMISSIONS.PARCEIROS.READ,
      PERMISSIONS.PARCEIROS.CREATE,
      PERMISSIONS.COMISSOES.READ,
    ].map(perm => ({
      id: perm,
      name: `Acesso de leitura a ${perm.split(':')[0]}`,
      description: `Permissão para ${perm.split(':')[1]} em ${perm.split(':')[0]}`,
      resource: perm.split(':')[0],
      action: perm.split(':')[1] as string,
    })),
  },
  {
    role: 'financeiro',
    permissions: [
      // Financeiro tem acesso a relatórios e dados financeiros
      PERMISSIONS.LEADS.READ,
      PERMISSIONS.CLIENTS.READ,
      PERMISSIONS.PROPOSALS.READ,
      PERMISSIONS.CONTRACTS.READ,
      PERMISSIONS.REPORTS.READ,
      PERMISSIONS.REPORTS.EXPORT,
      PERMISSIONS.SETTINGS.READ,
    ].map(perm => ({
      id: perm,
      name: `Acesso financeiro a ${perm.split(':')[0]}`,
      description: `Permissão para ${perm.split(':')[1]} em ${perm.split(':')[0]}`,
      resource: perm.split(':')[0],
      action: perm.split(':')[1] as string,
    })),
  },
  {
    role: 'ai_agent',
    permissions: [
      // Agente de IA tem acesso amplo para automação
      ...Object.values(PERMISSIONS.LEADS).map(perm => ({
        id: perm,
        name: `IA - Gestão de Leads`,
        description: `Acesso de IA para ${perm.split(':')[1]} em leads`,
        resource: 'leads',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.CLIENTS).map(perm => ({
        id: perm,
        name: `IA - Gestão de Clientes`,
        description: `Acesso de IA para ${perm.split(':')[1]} em clientes`,
        resource: 'clients',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.PROPOSALS).map(perm => ({
        id: perm,
        name: `IA - Gestão de Propostas`,
        description: `Acesso de IA para ${perm.split(':')[1]} em propostas`,
        resource: 'proposals',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.REPORTS).map(perm => ({
        id: perm,
        name: `IA - Relatórios`,
        description: `Acesso de IA para ${perm.split(':')[1]} em relatórios`,
        resource: 'reports',
        action: perm.split(':')[1] as string,
      })),
      ...Object.values(PERMISSIONS.DIMENSIONING).map(perm => ({
        id: perm,
        name: `IA - Dimensionamento`,
        description: `Acesso de IA para ${perm.split(':')[1]} em dimensionamento`,
        resource: 'dimensioning',
        action: perm.split(':')[1] as string,
      })),
    ],
  },
];

// Função para obter permissões de um perfil específico
export const getRolePermissions = (role: UserRole): Permission[] => {
  if (!role) return [];
  try {
    const rolePerms = ROLE_PERMISSIONS.find(rp => rp.role === role);
    return rolePerms?.permissions || [];
  } catch (error) {
    console.error('Erro ao obter permissões do perfil:', error);
    return [];
  }
};

// Função para verificar se um perfil tem uma permissão específica
export const hasPermission = (role: UserRole, permission: string): boolean => {
  if (!role || !permission) return false;
  try {
    const rolePerms = getRolePermissions(role);
    return rolePerms.some(perm => perm.id === permission);
  } catch (error) {
    console.error('Erro ao verificar permissão:', error);
    return false;
  }
};

// Função para verificar se um perfil tem acesso a um recurso
export const hasResourceAccess = (role: UserRole, resource: string, action: string): boolean => {
  if (!role || !resource || !action) return false;
  try {
    const rolePerms = getRolePermissions(role);
    return rolePerms.some(perm => 
      perm.resource === resource && 
      (perm.action === action || perm.action === 'manage')
    );
  } catch (error) {
    console.error('Erro ao verificar acesso ao recurso:', error);
    return false;
  }
};

